/**
 * The guest demo's End-demo confirm (design `docs/design/guest-demo.md` §5.6.4; owner ruling
 * 2026-10-24, "option 3 for end demo"; story `q-guest-end-demo-one-tap`). Pure, so the rule that
 * keeps one stray tap from ending the demo is unit-tested, not something a reviewer films.
 *
 * WHY: on a phone a ground tap a few pixels from an always-visible "End demo" button is snapped
 * onto the button by the browser and ended the demo with no question (`[guest] demo ended: left`).
 * Two guards, both required:
 *   1. WHERE — End demo lives in the Menu (the ☰ sheet; on desktop the launcher bar), never on the
 *      play area, and pressing it only OPENS this confirm.
 *   2. WHEN — the confirm control is a different control in a different place (a centred dialog,
 *      not under the finger that opened it), and it ignores presses for `END_CONFIRM_ARM_MS` after
 *      it appears, so a double-tap or a snapped touch landing on it as it opens does nothing. It
 *      also gives up on its own after `END_CONFIRM_TIMEOUT_MS` — walking away returns to play.
 *
 * The arm window is 800 ms: longer than a double-tap (~300 ms) and a touch-then-click echo
 * (~350 ms), shorter than a player's read of a five-word question.
 */

/** Presses on the confirm control are ignored for this long after the dialog opens (ms). */
export const END_CONFIRM_ARM_MS = 800;

/** The dialog closes itself, back to play, after this long without an answer (ms). */
export const END_CONFIRM_TIMEOUT_MS = 8000;

/** True when a confirm press at `now` counts: the dialog opened at `armedAt` and has armed. */
export function confirmCounts(armedAt: number, now: number): boolean {
  return now - armedAt >= END_CONFIRM_ARM_MS && now - armedAt < END_CONFIRM_TIMEOUT_MS;
}

export type EndConfirmState = { phase: 'idle' } | { phase: 'asking'; askedAt: number };

export type EndConfirmEvent =
  | { t: 'open'; now: number }
  | { t: 'confirm'; now: number }
  | { t: 'cancel' };

/**
 * The whole End-demo flow as one step function (the Shell holds the state; the dialog only
 * reports presses). `end` is true on exactly one transition: a confirm press that has armed and
 * has not timed out. Opening the dialog never ends; a confirm with no dialog open never ends; a
 * second Menu press while asking does not re-arm (it cannot be used to skip the arm window).
 */
export function endConfirmStep(
  state: EndConfirmState,
  ev: EndConfirmEvent,
): { state: EndConfirmState; end: boolean } {
  if (ev.t === 'open') {
    return {
      state: state.phase === 'asking' ? state : { phase: 'asking', askedAt: ev.now },
      end: false,
    };
  }
  if (ev.t === 'cancel' || state.phase === 'idle') return { state: { phase: 'idle' }, end: false };
  if (!confirmCounts(state.askedAt, ev.now)) {
    // Too early: the press is ignored and the dialog stays. Too late: it already timed out.
    return ev.now - state.askedAt >= END_CONFIRM_TIMEOUT_MS
      ? { state: { phase: 'idle' }, end: false }
      : { state, end: false };
  }
  return { state: { phase: 'idle' }, end: true };
}

/** The dialog's words. */
export const END_CONFIRM_COPY = {
  heading: 'End demo?',
  line: 'Your run is not saved. You can start another any time.',
  confirm: 'End demo',
  cancel: 'Keep playing',
  menuLabel: 'End demo',
} as const;
